import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import CreateIssueForm, { IssueCreatedBanner } from "../components/CreateIssueForm";
import ImageModal from "../components/ImageModal";

function VideoPlayer({ src, onDownload, maxWidth = 800 }) {
    const [error, setError] = useState(false);
    if (error) {
        return (
            <div style={{
                padding: 20, background: "var(--bg-secondary)", borderRadius: 8,
                border: "1px solid var(--border)", maxWidth, textAlign: "center",
            }}>
                <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 12 }}>
                    Video could not be played in the browser. Download to play locally.
                </p>
                <button
                    onClick={async () => {
                        try { await onDownload(); } catch (err) { alert("Download failed: " + err.message); }
                    }}
                    style={{
                        padding: "8px 20px", fontSize: 13, fontWeight: 600,
                        background: "var(--accent)", color: "#fff",
                        border: "none", borderRadius: 6, cursor: "pointer",
                    }}
                >
                    Download Video
                </button>
            </div>
        );
    }
    return (
        <video
            src={src}
            controls
            onError={() => setError(true)}
            style={{ width: "100%", maxWidth, borderRadius: 8 }}
        />
    );
}

function DipReviewPage() {
    const { reviewId } = useParams();
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const { hasPermission } = useAuth();
    const currentAccount = accounts[0];
    const canManage = hasPermission("reviews.manage");

    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(false);
    const [showCloseDialog, setShowCloseDialog] = useState(false);
    const [issueCount, setIssueCount] = useState(0);
    const [creatingIssueFor, setCreatingIssueFor] = useState(null);
    const [issueResults, setIssueResults] = useState({});
    const [modalImages, setModalImages] = useState(null);
    const [modalIndex, setModalIndex] = useState(0);

    const issueFormRef = useRef(null);
    const completedRef = useRef(false);
    const reviewerEmail = currentAccount?.username || "web_user";
    const reviewerName = currentAccount?.name || currentAccount?.username || "Web User";

    useEffect(() => {
        const init = async () => {
            try {
                if (canManage) {
                    await api.dips.startReview(reviewId);
                }
                const data = await api.dips.reviewDetail(reviewId);
                setReview(data);
                setIssueCount(data.issues_raised || 0);
            } catch (err) {
                console.error("Failed to load dip review:", err);
            } finally {
                setLoading(false);
            }
        };
        init();

        // Best-effort abandon on tab/window close
        const handleUnload = () => {
            if (!completedRef.current && canManage) {
                api.dips.abandonReview(reviewId).catch(() => {});
            }
        };
        window.addEventListener("beforeunload", handleUnload);

        // Cleanup on React unmount (SPA navigation)
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
            if (!completedRef.current && canManage) {
                api.dips.abandonReview(reviewId).catch(() => {});
            }
        };
    }, [reviewId]); // eslint-disable-line

    const handleClose = async (markReviewed) => {
        if (completing) return;
        setShowCloseDialog(false);
        if (markReviewed) {
            setCompleting(true);
            try {
                await api.dips.completeReview(reviewId, {
                    issues_raised: issueCount,
                    review_notes: null,
                });
                completedRef.current = true;
            } catch (err) {
                console.error("Failed to complete review:", err);
            }
        } else if (markReviewed === false) {
            // "keep as in progress" — leave the review row as-is
        }
        navigate("/reviews");
    };

    const handleAbandon = async () => {
        setShowCloseDialog(false);
        completedRef.current = true; // prevent unmount handler from firing again
        try {
            await api.dips.abandonReview(reviewId);
        } catch (err) {
            console.error("Failed to abandon review:", err);
        }
        navigate("/reviews");
    };

    const handleIssueCreated = (key, result) => {
        setIssueResults(prev => ({ ...prev, [key]: result }));
        setCreatingIssueFor(null);
        setIssueCount(n => n + 1);
    };

    if (loading) return <LoadingSpinner message="Loading dip review..." />;

    if (!review) {
        return (
            <div className="empty-state">
                <h3>Review not found</h3>
                <button className="retry-btn" onClick={() => navigate("/reviews")}>Back to Reviews</button>
            </div>
        );
    }

    const tanks = review.tanks || [];
    const formPhotos = review.formPhotos || [];
    const stills = review.stills || [];

    return (
        <div>
            {/* Sticky review bar */}
            <div style={{
                position: "sticky", top: 0, zIndex: 100,
                background: "var(--bg-primary)", borderBottom: "2px solid var(--border)",
                padding: "12px 20px", display: "flex", alignItems: "center", gap: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
            }}>
                <button
                    onClick={canManage ? handleAbandon : () => navigate("/reviews")}
                    style={{
                        padding: "5px 10px", fontSize: 13, cursor: "pointer",
                        background: "transparent", border: "1px solid var(--border)",
                        borderRadius: 6, color: "var(--text-muted)"
                    }}
                >
                    {canManage ? "← Exit without reviewing" : "← Back to Reviews"}
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                        <span style={{
                            fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                            background: "rgba(59,130,246,0.15)", color: "#3b82f6", marginRight: 8,
                        }}>DIP REVIEW</span>
                        {review.store_name || review.site_id}
                        <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                            #{review.site_id}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        Submitted by {review.submitted_by || "--"} on{" "}
                        {review.submitted_at ? new Date(review.submitted_at).toLocaleString() : "--"}
                        {review.dip_time && (
                            <span style={{ marginLeft: 12 }}>
                                Dip time: {new Date(review.dip_time).toLocaleString()}
                            </span>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {issueCount > 0 && (
                        <span style={{
                            fontSize: 12, fontWeight: 600, padding: "3px 10px",
                            background: "rgba(249,115,22,0.1)", color: "#f97316", borderRadius: 10
                        }}>
                            {issueCount} issue{issueCount !== 1 ? "s" : ""} raised
                        </span>
                    )}
                    {canManage && (
                        <button
                            onClick={() => setShowCloseDialog(true)}
                            disabled={completing}
                            style={{
                                padding: "7px 18px", fontSize: 13, fontWeight: 600,
                                background: completing ? "#aaa" : "#22c55e", color: "#fff",
                                border: "none", borderRadius: 6, cursor: completing ? "default" : "pointer"
                            }}
                        >
                            {completing ? "Saving..." : "Close Review"}
                        </button>
                    )}
                </div>
            </div>

            {/* Close dialog — 3 states */}
            {showCloseDialog && (
                <div style={{
                    position: "fixed", inset: 0, zIndex: 200,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                    <div style={{
                        background: "var(--bg-primary)", borderRadius: 10,
                        padding: "28px 32px", maxWidth: 400, width: "90%",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
                    }}>
                        <h3 style={{ margin: "0 0 8px", fontSize: 17 }}>Close Review</h3>
                        <p style={{ margin: "0 0 20px", color: "var(--text-secondary)", fontSize: 14 }}>
                            Is this review complete?
                            {issueCount > 0 && ` (${issueCount} issue${issueCount !== 1 ? "s" : ""} raised)`}
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <button onClick={() => handleClose(true)} style={{
                                padding: "10px 16px", fontSize: 14, fontWeight: 600,
                                background: "#22c55e", color: "#fff",
                                border: "none", borderRadius: 6, cursor: "pointer", textAlign: "left"
                            }}>Yes — mark as reviewed & close</button>
                            <button onClick={() => handleClose(false)} style={{
                                padding: "10px 16px", fontSize: 14, fontWeight: 600,
                                background: "#3b82f6", color: "#fff",
                                border: "none", borderRadius: 6, cursor: "pointer", textAlign: "left"
                            }}>No — keep as in progress & close</button>
                            <button onClick={handleAbandon} style={{
                                padding: "10px 16px", fontSize: 14,
                                background: "transparent", color: "var(--text-secondary)",
                                border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", textAlign: "left"
                            }}>Close without reviewing</button>
                            <button onClick={() => setShowCloseDialog(false)} style={{
                                padding: "8px 16px", fontSize: 13,
                                background: "transparent", color: "var(--text-muted)",
                                border: "none", borderRadius: 6, cursor: "pointer"
                            }}>Cancel — keep reviewing</button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ padding: 20 }}>
                {/* Tank data from form submission */}
                <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                        Tank Data ({tanks.length} tank{tanks.length !== 1 ? "s" : ""})
                    </h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                        {tanks.map(t => {
                            const hasIssue = t.fill_point_closed === false || t.seals_ok === false
                                || t.dipstick_seal_ok === false || t.water_paste_result === "water";
                            return (
                                <div key={t.id} style={{
                                    background: "var(--bg-secondary)", borderRadius: 8, padding: "10px 14px",
                                    border: `1px solid ${hasIssue ? "#ef4444" : "var(--border)"}`,
                                    minWidth: 200, flex: "1 1 220px",
                                }}>
                                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                                        Tank {t.tank_number} — {t.product || "?"}
                                    </div>
                                    {t.dip_measure_litres != null && (
                                        <div style={{ fontSize: 12, marginBottom: 4 }}>Volume: {Number(t.dip_measure_litres).toLocaleString()}L</div>
                                    )}
                                    <div style={{ fontSize: 12, color: t.water_paste_result === "water" ? "#ef4444" : t.water_paste_result === "na" ? "var(--text-secondary)" : "#22c55e" }}>
                                        Paste test: {t.water_paste_result === "water" ? "WATER DETECTED" : t.water_paste_result === "clear" ? "Clear" : t.water_paste_result === "na" ? "N/A" : t.water_paste_result || "--"}
                                    </div>
                                    <div style={{ fontSize: 12, color: t.fill_point_closed === false ? "#ef4444" : t.fill_point_closed === true ? "#22c55e" : "var(--text-secondary)" }}>
                                        Fill point closed: {t.fill_point_closed === true ? "Yes" : t.fill_point_closed === false ? "No" : "--"}
                                    </div>
                                    <div style={{ fontSize: 12, color: t.seals_ok === false ? "#ef4444" : t.seals_ok === true ? "#22c55e" : "var(--text-secondary)" }}>
                                        Seals: {t.seals_ok === true ? "OK" : t.seals_ok === false ? "Damaged" : "--"}
                                    </div>
                                    <div style={{ fontSize: 12, color: t.dipstick_seal_ok === false ? "#ef4444" : t.dipstick_seal_ok === true ? "#22c55e" : "var(--text-secondary)" }}>
                                        Dipstick rubber: {t.dipstick_seal_ok === true ? "OK" : t.dipstick_seal_ok === false ? "Damaged" : "--"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Form photos — grouped by tank */}
                {formPhotos.length > 0 && (() => {
                    // Group photos by tank
                    const byTank = {};
                    formPhotos.forEach((p, i) => {
                        const key = p.tankNumber ? `tank_${p.tankNumber}` : "other";
                        if (!byTank[key]) byTank[key] = { tankNumber: p.tankNumber, product: p.product, photos: [] };
                        byTank[key].photos.push({ ...p, globalIdx: i });
                    });
                    return (
                        <div style={{ marginBottom: 24 }}>
                            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                                Form Photos ({formPhotos.length})
                            </h3>
                            {Object.values(byTank).map(group => (
                                <div key={group.tankNumber || "other"} style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>
                                        {group.tankNumber ? `Tank ${group.tankNumber}` : "Photos"}
                                        {group.product && ` — ${group.product}`}
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                        {group.photos.map((p) => {
                                            const key = `photo_${p.globalIdx}`;
                                            return (
                                                <div key={p.globalIdx} style={{ position: "relative" }}>
                                                    <img
                                                        src={p.url}
                                                        alt={`Tank ${group.tankNumber || "?"} photo`}
                                                        onClick={() => {
                                                            const allPhotos = formPhotos.map(fp => ({ url: fp.url, label: fp.tankNumber ? `Tank ${fp.tankNumber}` : "Photo" }));
                                                            setModalImages(allPhotos);
                                                            setModalIndex(p.globalIdx);
                                                        }}
                                                        style={{
                                                            width: 160, height: 120, objectFit: "cover",
                                                            borderRadius: 6, cursor: "pointer",
                                                            border: "1px solid var(--border)",
                                                        }}
                                                    />
                                                    {canManage && !issueResults[key] && (
                                                        <button
                                                            onClick={() => setCreatingIssueFor(key)}
                                                            style={{
                                                                position: "absolute", top: 4, right: 4,
                                                                fontSize: 10, fontWeight: 600, padding: "2px 6px",
                                                                background: "rgba(239,68,68,0.8)", color: "#fff",
                                                                border: "none", borderRadius: 3, cursor: "pointer",
                                                            }}
                                                            title="Raise issue from this photo"
                                                        >
                                                            Flag
                                                        </button>
                                                    )}
                                                    {issueResults[key] && (
                                                        <IssueCreatedBanner result={issueResults[key]} compact />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Inline issue form — appears right below the flagged photo's tank group */}
                                    {creatingIssueFor && group.photos.some(p => creatingIssueFor === `photo_${p.globalIdx}`) && (
                                        <div ref={el => { if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100); }} style={{
                                            background: "var(--bg-primary)", borderTop: "2px solid var(--border)",
                                            padding: 20, marginTop: 12, borderRadius: 8,
                                            border: "1px solid var(--border)", boxShadow: "0 -4px 16px rgba(0,0,0,0.15)"
                                        }}>
                                            <CreateIssueForm
                                                siteId={review.site_id}
                                                storeId={review.store_id}
                                                storeName={review.store_name}
                                                zone="dip_form"
                                                category="fuel_compliance"
                                                defaultSeverity="warning"
                                                imageUrl={formPhotos[parseInt(creatingIssueFor.split("_")[1], 10)]?.url}
                                                onCreated={(result) => handleIssueCreated(creatingIssueFor, result)}
                                                onCancel={() => setCreatingIssueFor(null)}
                                                currentUser={currentAccount}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {/* Video */}
                {!review.videoUrl && (
                    <div style={{
                        marginBottom: 24, padding: "12px 16px", borderRadius: 8,
                        background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.3)",
                        color: "#f97316", fontSize: 13, fontWeight: 500,
                    }}>
                        Video unavailable {review.videoNote ? `- ${review.videoNote}` : "- download failed or not yet processed"}
                    </div>
                )}
                {review.videoUrl && (
                    <div style={{ marginBottom: 24 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                            Video Evidence
                            <button
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                        const result = await api.dips.reviewVideoDownload(reviewId);
                                        if (result.url) window.location.href = result.url;
                                    } catch (err) {
                                        alert("Download failed: " + err.message);
                                    }
                                }}
                                style={{
                                    marginLeft: 12, fontSize: 12, fontWeight: 500,
                                    color: "var(--accent)", background: "none", border: "none",
                                    cursor: "pointer", textDecoration: "underline",
                                }}
                            >
                                Download
                            </button>
                        </h3>
                        <VideoPlayer
                            src={review.videoUrl}
                            onDownload={async () => {
                                const result = await api.dips.reviewVideoDownload(reviewId);
                                if (result.url) window.location.href = result.url;
                            }}
                        />
                    </div>
                )}

                {/* Video stills */}
                {stills.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                            Video Stills — every 30s ({stills.length} frames)
                        </h3>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {stills.map((s, i) => (
                                <div key={i} style={{ position: "relative" }}>
                                    <img
                                        src={s.url}
                                        alt={`Still ${i + 1}`}
                                        onClick={() => {
                                            setModalImages(stills.map((st, si) => ({
                                                url: st.url,
                                                label: `${Math.floor(si * 30 / 60)}:${String(si * 30 % 60).padStart(2, "0")}`,
                                            })));
                                            setModalIndex(i);
                                        }}
                                        style={{
                                            width: 200, height: 140, objectFit: "cover",
                                            borderRadius: 6, cursor: "pointer",
                                            border: "1px solid var(--border)",
                                        }}
                                    />
                                    <span style={{
                                        position: "absolute", bottom: 4, left: 4,
                                        fontSize: 10, fontWeight: 600, padding: "1px 5px",
                                        background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: 3,
                                    }}>
                                        {Math.floor(i * 30 / 60)}:{String(i * 30 % 60).padStart(2, "0")}
                                    </span>
                                    {canManage && !issueResults[`still_${i}`] && (
                                        <button
                                            onClick={() => setCreatingIssueFor(`still_${i}`)}
                                            style={{
                                                position: "absolute", top: 4, right: 4,
                                                fontSize: 10, fontWeight: 600, padding: "2px 6px",
                                                background: "rgba(239,68,68,0.8)", color: "#fff",
                                                border: "none", borderRadius: 3, cursor: "pointer",
                                            }}
                                            title="Raise issue from this still"
                                        >
                                            Flag
                                        </button>
                                    )}
                                    {issueResults[`still_${i}`] && (
                                        <IssueCreatedBanner result={issueResults[`still_${i}`]} compact />
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* Inline issue form for stills */}
                        {creatingIssueFor && creatingIssueFor.startsWith("still_") && (
                            <div ref={el => { if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 100); }} style={{
                                background: "var(--bg-primary)", borderTop: "2px solid var(--border)",
                                padding: 20, marginTop: 12, borderRadius: 8,
                                border: "1px solid var(--border)", boxShadow: "0 -4px 16px rgba(0,0,0,0.15)"
                            }}>
                                <CreateIssueForm
                                    siteId={review.site_id}
                                    storeId={review.store_id}
                                    storeName={review.store_name}
                                    zone="dip_form"
                                    category="fuel_compliance"
                                    defaultSeverity="warning"
                                    imageUrl={stills[parseInt(creatingIssueFor.split("_")[1], 10)]?.url}
                                    onCreated={(result) => handleIssueCreated(creatingIssueFor, result)}
                                    onCancel={() => setCreatingIssueFor(null)}
                                    currentUser={currentAccount}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Notes */}
                {review.submission_notes && (
                    <div style={{ marginBottom: 24 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Notes</h3>
                        <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>{review.submission_notes}</p>
                    </div>
                )}

                {/* Issue form is now rendered inline next to the flagged image */}
            </div>

            {/* Image lightbox with zoom/pan/nav */}
            {modalImages && (
                <ImageModal
                    images={modalImages}
                    currentIndex={modalIndex}
                    onClose={() => setModalImages(null)}
                    onPrev={modalIndex > 0 ? () => setModalIndex(modalIndex - 1) : null}
                    onNext={modalIndex < modalImages.length - 1 ? () => setModalIndex(modalIndex + 1) : null}
                />
            )}
        </div>
    );
}

export default DipReviewPage;
